import { useEffect, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { KoppelingRow } from '@fleet/shared';
import { DATUM_GRENZEN, datumPlausibel, fmtDate, isIsoDatum } from '@fleet/shared';
import { Badge, Button } from '../../design-system';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { Panel } from '../../components/ui/Panel';
import { ApiError } from '../../lib/apiClient';
import { KoppelDoelLink } from './KoppelDoelLink';
import { useDeleteKoppeling, useUpdateKoppeling } from './personen.api';

const dateStyle: CSSProperties = {
  fontFamily: 'inherit',
  fontSize: 13,
  padding: '6px 8px',
  border: '1px solid #C9BCA9',
  borderRadius: 4,
  background: '#FBF5EE',
  color: '#111111',
};

/**
 * Eén bewerkbare datum in de historie.
 *
 * Opslaan gebeurt bij het verlaten van het veld (of op Enter), niet per
 * toetsaanslag. Een `<input type="date">` vuurt in Chrome een change-event per
 * cijfer dat je in een segment typt: typ "2" in het jaar en de waarde is
 * `0002-07-15`, een geldige datum. Toen dat meteen werd opgeslagen ging elke
 * tussenstand naar de server, werd het veld tijdens het verzoek uitgeschakeld
 * (focus weg, typvolgorde afgebroken) en bleef het jaar op 0002 hangen.
 *
 * Een datum buiten het gewone bereik (`DATUM_GRENZEN.plausibel`) wordt niet
 * geweigerd maar nagevraagd: een koppeling uit 1998 of over drie jaar kán de
 * bedoeling zijn, en wie veel historie corrigeert wil niet tegen een muur lopen.
 * Wat nooit de bedoeling kan zijn (geen kalenderdatum, jaar buiten 1900 tot
 * 2100) houdt de server tegen.
 */
function DatumCel({
  label,
  waarde,
  leegToegestaan,
  onOpslaan,
}: {
  label: ReactNode;
  /** De opgeslagen waarde; leeg als er (nog) geen datum is. */
  waarde: string;
  /** Of wissen mag; bij een einddatum betekent leeg "nog actief". */
  leegToegestaan: boolean;
  onOpslaan: (nieuw: string | null) => Promise<void>;
}) {
  const [tekst, setTekst] = useState(waarde);
  const [bezig, setBezig] = useState(false);
  const [teBevestigen, setTeBevestigen] = useState<string | null>(null);

  // Van buiten bijgewerkt (na een geslaagde opslag of een andere correctie):
  // het veld volgt, tenzij er net een bevestiging openstaat.
  useEffect(() => {
    if (teBevestigen === null) setTekst(waarde);
  }, [waarde, teBevestigen]);

  async function sla(nieuw: string | null) {
    setBezig(true);
    try {
      await onOpslaan(nieuw);
    } catch {
      // De melding toont de historie zelf; hier alleen terug naar wat er stond.
      setTekst(waarde);
    } finally {
      setBezig(false);
      setTeBevestigen(null);
    }
  }

  function bevestigOfSla() {
    if (tekst === waarde) return;
    if (!tekst) {
      if (leegToegestaan) void sla(null);
      else setTekst(waarde);
      return;
    }
    if (!isIsoDatum(tekst)) {
      setTekst(waarde);
      return;
    }
    if (datumPlausibel(tekst)) void sla(tekst);
    else setTeBevestigen(tekst);
  }

  const nu = new Date().getFullYear();
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 11, color: '#888888' }}>
        {label}
        {bezig && <span style={{ marginLeft: 6, color: '#2A6085' }}>Opslaan…</span>}
      </span>
      <input
        type="date"
        style={dateStyle}
        value={tekst}
        onChange={(e) => setTekst(e.target.value)}
        onBlur={bevestigOfSla}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
      />
      {teBevestigen && (
        <ConfirmModal
          title="Ongebruikelijke datum"
          message={`${fmtDate(teBevestigen)} ligt buiten het gewone bereik (${DATUM_GRENZEN.plausibel.van} tot ${nu + DATUM_GRENZEN.plausibel.jarenVooruit}). Is dat de bedoeling?`}
          confirmLabel="Toch opslaan"
          busy={bezig}
          onConfirm={() => void sla(teBevestigen)}
          onClose={() => {
            setTeBevestigen(null);
            setTekst(waarde);
          }}
        />
      )}
    </label>
  );
}

/**
 * Link history. Read-only by default (the intervals are the source of truth,
 * edited via the koppelen/loskoppelen buttons). A protected "aanpassen" mode
 * exposes editable van/tot dates and row deletion for corrections.
 */
export function KoppelHistorie({ koppelingen }: { koppelingen: KoppelingRow[] }) {
  const update = useUpdateKoppeling();
  const del = useDeleteKoppeling();
  const [bewerken, setBewerken] = useState(false);
  const [error, setError] = useState('');
  const [teVerwijderen, setTeVerwijderen] = useState<KoppelingRow | null>(null);
  const [delError, setDelError] = useState('');

  async function wijzig(id: string, patch: { van?: string; tot?: string | null }) {
    setError('');
    try {
      await update.mutateAsync({ id, ...patch });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Kon datum niet wijzigen');
      throw e;
    }
  }

  async function doVerwijder() {
    if (!teVerwijderen) return;
    setDelError('');
    try {
      await del.mutateAsync(teVerwijderen.id);
      setTeVerwijderen(null);
    } catch (e) {
      setDelError(e instanceof ApiError ? e.message : 'Verwijderen mislukt');
    }
  }

  return (
    <Panel style={{ padding: '24px 28px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div>
          <Eyebrow color="#2A6085">Koppelhistorie</Eyebrow>
        </div>
        {koppelingen.length > 0 && (
          <Button
            variant={bewerken ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              setBewerken((b) => !b);
              setError('');
            }}
          >
            {bewerken ? 'Klaar' : 'Koppelhistorie aanpassen'}
          </Button>
        )}
      </div>

      {koppelingen.length === 0 && (
        <div style={{ fontSize: 13, color: '#888888', marginTop: 16 }}>Nog geen koppelingen.</div>
      )}

      {bewerken && (
        <div
          style={{
            marginTop: 16,
            padding: '10px 14px',
            borderRadius: 6,
            background: '#FBF0D9',
            border: '1px solid #E5C67A',
            fontSize: 12.5,
            color: '#7A5A16',
            lineHeight: 1.5,
          }}
        >
          <strong>Let op:</strong> handmatig aanpassen kan de kostentoewijzing verschuiven. Koppelen
          en loskoppelen doe je normaal met de knoppen bovenaan de pagina.
        </div>
      )}

      {error && (
        <div style={{ fontSize: 13, color: '#B0543B', fontWeight: 700, marginTop: 12 }}>
          {error}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          maxHeight: 360,
          overflowY: 'auto',
          paddingRight: 4,
          marginTop: 12,
        }}
      >
        {koppelingen.map((k) =>
          bewerken ? (
            <div
              key={k.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 140px 140px auto',
                gap: 12,
                alignItems: 'center',
                padding: '10px 0',
                borderTop: '1px solid #F0E8DC',
                fontSize: 13,
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Badge tone={k.doelType === 'PAS' ? 'blue' : 'success'}>{k.doelType}</Badge>
                <KoppelDoelLink koppeling={k} />
              </span>
              <DatumCel
                label="Van"
                waarde={k.van}
                leegToegestaan={false}
                onOpslaan={(nieuw) => wijzig(k.id, { van: nieuw ?? k.van })}
              />
              <DatumCel
                label={k.tot ? 'Tot' : 'Tot (actief)'}
                waarde={k.tot ?? ''}
                leegToegestaan
                onOpslaan={(nieuw) => wijzig(k.id, { tot: nieuw })}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setDelError('');
                  setTeVerwijderen(k);
                }}
                style={{ color: '#B0543B' }}
              >
                Verwijderen
              </Button>
            </div>
          ) : (
            <div
              key={k.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 0',
                borderTop: '1px solid #F0E8DC',
                fontSize: 13,
              }}
            >
              <Badge tone={k.doelType === 'PAS' ? 'blue' : 'success'}>{k.doelType}</Badge>
              <span style={{ flex: 1 }}>
                <KoppelDoelLink koppeling={k} />
              </span>
              <span
                style={{ color: '#666666', fontFamily: "'Roboto Mono', monospace", fontSize: 12 }}
              >
                {k.tot ? `${fmtDate(k.van)} – ${fmtDate(k.tot)}` : `sinds ${fmtDate(k.van)}`}
              </span>
              {!k.tot && <Badge tone="success">Actief</Badge>}
            </div>
          ),
        )}
      </div>

      {teVerwijderen && (
        <ConfirmModal
          title="Historie-regel verwijderen"
          message={`Weet je zeker dat je deze regel (${teVerwijderen.label}) wilt verwijderen? Dit kan de kostentoewijzing en de huidige koppeling wijzigen.`}
          confirmLabel="Verwijderen"
          danger
          busy={del.isPending}
          error={delError}
          onConfirm={doVerwijder}
          onClose={() => setTeVerwijderen(null)}
        />
      )}
    </Panel>
  );
}
